import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, Loader } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { verifyPaymentMethodUpdate as verifyPaymentMethodAPI } from "../api/apiFunction/billingService";

const PaymentMethodVerify = () => {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [paymentMethodDetails, setPaymentMethodDetails] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyPaymentMethod = async () => {
      try {
        // Get session_id and user_id from URL
        const sessionId = searchParams.get("session_id");
        const userId = searchParams.get("user_id");

        if (!sessionId) {
          setStatus("error");
          setMessage("Missing session ID. Please try again.");
          return;
        }

        // Call verify payment method API
        const res = await verifyPaymentMethodAPI(sessionId, userId || "");

        if (res?.status === 200 && res?.data?.success) {
          setStatus("success");
          setMessage(
            res.data.message || "Payment method updated successfully!"
          );

          // Set payment method details for display
          setPaymentMethodDetails({
            brand: res.data.paymentMethod?.brand || "Card",
            lastFour: res.data.paymentMethod?.lastFour || "****",
            expMonth: res.data.paymentMethod?.expMonth,
            expYear: res.data.paymentMethod?.expYear,
            holderName: res.data.paymentMethod?.holderName,
            maskedCard: res.data.paymentMethod?.maskedCard,
          });

          // Update localStorage user data if needed
          const user = localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user"))
            : null;

          if (user && res.data.paymentMethod) {
            user.stripePaymentMethodId = res.data.paymentMethod.id;
            localStorage.setItem("user", JSON.stringify(user));
          }
        } else {
          setStatus("error");
          setMessage(
            res?.data?.message ||
              res?.data?.error ||
              "Payment method update failed"
          );
        }
      } catch (error) {
        setStatus("error");

        const errorMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to verify payment method. Please contact support.";
        setMessage(errorMsg);

        toast.error(errorMsg);
      }
    };

    verifyPaymentMethod();
  }, [searchParams, navigate]);

  const handleGoToSettings = () => {
    navigate("/settings?tab=billing", { replace: true });
  };

  const handleRetry = () => {
    navigate("/settings?tab=billing", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-bg-50 px-4">
      <div className="max-w-md w-full text-center">
        {/* Status Icon */}
        <div className="mb-6">
          <div
            className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center ${
              status === "loading"
                ? "bg-blue-100 dark:bg-blue-900"
                : status === "success"
                ? "bg-green-100 dark:bg-green-900"
                : "bg-red-100 dark:bg-red-900"
            }`}
          >
            {status === "loading" && (
              <Loader className="w-10 h-10 text-blue-600 animate-spin" />
            )}
            {status === "success" && (
              <CheckCircle className="w-10 h-10 text-green-600" />
            )}
            {status === "error" && (
              <XCircle className="w-10 h-10 text-red-600" />
            )}
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold mb-4 text-fg-50">
          {status === "loading" && "Updating Payment Method..."}
          {status === "success" && "Payment Method Updated! 🎉"}
          {status === "error" && "Update Failed"}
        </h1>

        {/* Message */}
        <p className="text-fg-60 text-lg mb-8">
          {status === "loading" &&
            "Please wait while we update your payment method..."}
          {status === "success" &&
            "Your payment method has been successfully updated."}
          {status === "error" &&
            (message || "Something went wrong. Please try again.")}
        </p>

        {/* Payment Method Details (Success Only) */}
        {status === "success" && paymentMethodDetails && (
          <div className="bg-bg-60 rounded-xl p-6 mb-8 text-left">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-semibold text-fg-50">New Payment Method</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-fg-60">Card Type</span>
                <span className="text-fg-50 font-medium capitalize">
                  {paymentMethodDetails.brand}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-fg-60">Card Number</span>
                <span className="text-fg-50 font-medium">
                  {paymentMethodDetails.maskedCard ||
                    `•••• ${paymentMethodDetails.lastFour}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-fg-60">Expires</span>
                <span className="text-fg-50 font-medium">
                  {paymentMethodDetails.expMonth}/{paymentMethodDetails.expYear}
                </span>
              </div>
              {paymentMethodDetails.holderName && (
                <div className="flex justify-between">
                  <span className="text-fg-60">Card Holder</span>
                  <span className="text-fg-50 font-medium">
                    {paymentMethodDetails.holderName}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {status === "success" && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleGoToSettings}
              className="bg-transparent border border-bd-50 text-fg-50 py-3 px-4 rounded-full font-medium hover:bg-bg-60 transition-all duration-200"
            >
              Go to Settings
            </button>
            <button
              onClick={() => navigate("/chat")}
              className="bg-ac-02 text-white dark:text-black py-3 px-4 rounded-full font-medium hover:bg-ac-01 transition-all duration-200"
            >
              Go to Chat
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate("/")}
              className="bg-transparent border border-bd-50 text-fg-50 py-3 px-4 rounded-full font-medium hover:bg-bg-60 transition-all duration-200"
            >
              Back to Home
            </button>
            <button
              onClick={handleRetry}
              className="bg-ac-02 text-white dark:text-black py-3 px-4 rounded-full font-medium hover:bg-ac-01 transition-all duration-200"
            >
              Back to Settings
            </button>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-8 text-sm text-fg-60">
          <p>
            Need help? Contact our{" "}
            <a
              href="/contact"
              className="text-ac-02 hover:text-ac-01 underline"
            >
              support team
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodVerify;
